/**
 * IIFE entry point: bundled for `<script>` tag consumers.
 *
 * Script-tag users who include the global build don't need extra script tags
 * or registration calls: setting `features.streamAnimation.type` to any
 * built-in name just works. `letter-rise` and `word-fade` are core built-ins;
 * `wipe` and `glyph-cycle` live in the lazy `animations-extra.js` sibling
 * chunk, fetched by the UI the first time config selects one.
 *
 * npm consumers continue to import from the main entry (`import ... from
 * "@runtypelabs/persona"`): those animations stay in their subpath
 * modules so bundlers can tree-shake them.
 */

// Re-export the full public API: from `index-core` (NOT `index`) so the
// dev-only helpers (`generateCodeSnippet`, `createDemoCarousel`) stay out of the
// CDN/IIFE bundle. npm consumers still get them via the `index.ts` barrel.
export * from "./index-core";

// Expose plugin-registration helpers on the global so custom animations
// can be registered from inline `<script>` blocks or third-party CDN scripts.
export {
  registerStreamAnimationPlugin,
  unregisterStreamAnimationPlugin,
  listRegisteredStreamAnimations,
} from "./utils/stream-animation";
export type { StreamAnimationPlugin, StreamAnimationContext } from "./types";

// ---------------------------------------------------------------------------
// Deferred WebMCP polyfill loading.
//
// This bundle is built with `@mcp-b/webmcp-polyfill` external: the bridge's
// default `import("@mcp-b/webmcp-polyfill")` is a bare specifier no browser
// can resolve, so register a loader that imports the self-contained
// `webmcp-polyfill.js` chunk from a sibling URL instead. Mirrors how
// `install.ts` derives `launcher.global.js` from a `jsUrl` override.
// ---------------------------------------------------------------------------

import { setWebMcpPolyfillLoader } from "./webmcp-bridge";
import { pinCdnRelease } from "./utils/cdn-release";

// Capture at module-evaluation time: `document.currentScript` is null once
// execution leaves the script's initial synchronous run.
const widgetScriptSrc: string | null =
  typeof document !== "undefined"
    ? pinCdnRelease((document.currentScript as HTMLScriptElement | null)?.src ?? "") || null
    : null;

/**
 * Loader for a lazy chunk published next to `index.global.js` (every
 * `set*Loader` below uses it). Derives the chunk URL from this script's URL;
 * self-hosted deployments that rename the bundle get an error naming the
 * `remedy`.
 */
const siblingChunk =
  <T>(file: string, remedy = `host ${file} alongside it`) => {
  let failures = 0;
  return (): Promise<T> => {
    const chunkUrl = widgetScriptSrc?.replace(/index\.global\.js($|\?)/, `${file}$1`);
    if (!chunkUrl || chunkUrl === widgetScriptSrc) {
      return Promise.reject(
        new Error(
          `Could not derive the ${file} URL from the widget script URL ` +
            `(${widgetScriptSrc ?? "unavailable"}). Self-hosted deployments that ` +
            `rename index.global.js should ${remedy}.`,
        ),
      );
    }
    // Runtime-only dynamic import; the specifier is a computed URL, so the bundler
    // leaves it untouched (and must not try to bundle it).
    // Browsers cache failed module fetches too. Give explicit retries a fresh
    // module URL; successful imports continue to share the chunk loader cache.
    return import(/* @vite-ignore */ `${chunkUrl}${failures ? `#retry-${failures}` : ""}`).catch((error: unknown) => {
      failures++;
      throw error;
    });
  };
};

setWebMcpPolyfillLoader(
  siblingChunk(
    "webmcp-polyfill.js",
    "install @mcp-b/webmcp-polyfill on the page themselves before enabling config.webmcp",
  ),
);

// ---------------------------------------------------------------------------
// Deferred Markdown Parsers (marked + dompurify) loading.
//
// This bundle is built with `./markdown-parsers-entry` external: the Markdown
// and HTML sanitization libraries are kept out of the CDN payload. Register a
// loader that imports the self-contained `markdown-parsers.js` chunk from a
// sibling URL. Renderer preparation loads it before revealing the full widget.
// Deferred installs keep using the critical launcher until that preparation finishes.
// ---------------------------------------------------------------------------

import { setMarkdownParsersLoader } from "./markdown-parsers-loader";

setMarkdownParsersLoader(siblingChunk("markdown-parsers.js"));

// Deferred SSE stream processor (`client-stream.ts`), built with
// `./client-stream` external. The client starts the fetch when a dispatch
// begins and the UI warms it on first panel visibility.
import { setClientStreamLoader } from "./client-stream-loader";

setClientStreamLoader(siblingChunk("client-stream.js"));

// Deferred visitor-history REST (`client-history.ts`), built with
// `./client-history` external; fetched on the first history call.
import { setClientHistoryLoader } from "./client-history-loader";

setClientHistoryLoader(siblingChunk("client-history.js"));

// Deferred UI glue for opt-in / interaction-only features (`ui-extras-entry.ts`),
// built with `./ui-extras-entry` external; warmed when such a feature activates.
import { setUiExtrasLoader } from "./ui-extras-loader";

setUiExtrasLoader(siblingChunk("ui-extras.js"));
// Deferred session resolve paths (`session-actions.ts`: approvals,
// ask_user_question answers, WebMCP / suggest_replies resumes), built with
// `./session-actions` external. The session warms it when the agent pauses.
import { setSessionActionsLoader } from "./session-actions-loader";

setSessionActionsLoader(siblingChunk("session-actions.js"));
// Deferred visitor-history shell (`history-shell.ts`), built with
// `./history-shell` external. Fetched at mount only when
// `features.history.enabled`, or on the first history API call.
import { setHistoryShellLoader } from "./history-shell-loader";

setHistoryShellLoader(siblingChunk("history-shell.js"));

// ---------------------------------------------------------------------------
// Deferred Runtype TTS engine loading.
//
// This bundle is built with `./runtype-speech-engine` external (see
// `tsdown.config.ts`): the hosted read-aloud engine + the
// AudioPlaybackManager it bundles are kept out of the CDN payload. Register a
// loader that imports the self-contained `runtype-tts.js` chunk from a sibling
// URL; the session prefetches it at init when `textToSpeech.provider:'runtype'`
// is set, so it's warm before the first click. Same pattern as the WebMCP
// polyfill above.
// ---------------------------------------------------------------------------

import { setRuntypeTtsLoader } from "./voice/runtype-tts-loader";

setRuntypeTtsLoader(
  siblingChunk(
    "runtype-tts.js",
    "host runtype-tts.js alongside it, or set textToSpeech.createEngine to supply a speech engine directly",
  ),
);

// ---------------------------------------------------------------------------
// Deferred Context Mentions loading.
//
// This bundle is built with `./context-mentions-entry` external (see
// `tsdown.config.ts`): the mention controller/manager/menu runtime is kept
// out of the CDN payload. Register a loader that imports the self-contained
// `context-mentions.js` chunk from a sibling URL; the orchestrator calls it on
// the first `@`/affordance-button interaction (and can prefetch on composer
// focus) only when `contextMentions.enabled`. Same pattern as the chunks above.
// ---------------------------------------------------------------------------

import { setContextMentionsLoader } from "./context-mentions-loader";
import { setContextMentionsInlineLoader } from "./context-mentions-inline-loader";

setContextMentionsLoader(siblingChunk("context-mentions.js"));

// Sibling loader for the inline-mention contenteditable chunk, loaded on composer
// mount when `contextMentions.display === "inline"`. Same sibling-URL scheme.
setContextMentionsInlineLoader(siblingChunk("context-mentions-inline.js"));

// ---------------------------------------------------------------------------
// Deferred history-view loading.
//
// This bundle is built with `@runtypelabs/persona/history-view` external (see
// `tsdown.config.ts`): the Messages navigation surface is kept out of the
// CDN payload and fetched only when the visitor first opens history. Same
// sibling-URL scheme as the chunks above.
// ---------------------------------------------------------------------------

import { setHistoryViewLoader } from "./history-view-loader";

setHistoryViewLoader(siblingChunk("history-view.js"));

// ---------------------------------------------------------------------------
// Deferred stream-animations loading (wipe + glyph-cycle).
//
// This bundle is built with `@runtypelabs/persona/animations-extra` external
// (see `tsdown.config.ts`): the subpath animation plugins are kept out of
// the CDN payload and fetched only when `features.streamAnimation.type`
// selects one (see the lazy resolver in ui.ts). Same sibling-URL scheme as the
// chunks above.
// ---------------------------------------------------------------------------

import { setAnimationsExtraLoader } from "./animations-extra-loader";

setAnimationsExtraLoader(
  siblingChunk(
    "animations-extra.js",
    "host animations-extra.js alongside it, or register the animation plugins themselves via registerStreamAnimationPlugin",
  ),
);

// ---------------------------------------------------------------------------
// Deferred approval-ui loading.
//
// This bundle is built with `@runtypelabs/persona/approval-ui` external (see
// `tsdown.config.ts`): the approval bubble, built-in approval plugin, and
// plugin-kit are kept out of the CDN payload and fetched only when the first
// approval message arrives. Same sibling-URL scheme as the chunks above.
// ---------------------------------------------------------------------------

import { setApprovalUiLoader } from "./approval-ui-loader";

setApprovalUiLoader(siblingChunk("approval-ui.js"));

// ---------------------------------------------------------------------------
// Deferred voice-runtime loading.
//
// This bundle is built with `@runtypelabs/persona/voice-runtime` external (see
// `tsdown.config.ts`): the voice provider factory + providers + audio
// playback manager are kept out of the CDN payload, prefetched by the session
// when a voice provider is configured. Same sibling-URL scheme as the chunks
// above.
// ---------------------------------------------------------------------------

import { setVoiceRuntimeLoader } from "./voice-runtime-loader";

setVoiceRuntimeLoader(siblingChunk("voice-runtime.js"));

// The voice fast tier (`client_tools`), fetched by client.ts only when a voice
// call negotiates it. Same sibling-URL scheme.
import { setVoiceClientToolsLoader } from "./voice-client-tools-loader";

setVoiceClientToolsLoader(siblingChunk("voice-client-tools.js"));

// ---------------------------------------------------------------------------
// Deferred artifacts-ui loading.
//
// This bundle is built with `@runtypelabs/persona/artifacts-ui` external (see
// `tsdown.config.ts`): the artifact pane, inline/card components, and
// preview renderer are kept out of the CDN payload and fetched when the
// artifacts sidebar is enabled or the first artifact directive arrives. Same
// sibling-URL scheme as the chunks above.
// ---------------------------------------------------------------------------

import { setArtifactsUiLoader } from "./artifacts-ui-loader";

setArtifactsUiLoader(siblingChunk("artifacts-ui.js"));

// ---------------------------------------------------------------------------
// Deferred forms-ui loading.
//
// This bundle is built with `@runtypelabs/persona/forms-ui` external (see
// `tsdown.config.ts`): the `[data-tv-form]` demo-forms enhancement is
// kept out of the CDN payload and fetched when a rendered bubble first
// contains a form placeholder. Same sibling-URL scheme as the chunks above.
// ---------------------------------------------------------------------------

import { setFormsUiLoader } from "./forms-ui-loader";

setFormsUiLoader(siblingChunk("forms-ui.js"));

// ---------------------------------------------------------------------------
// Deferred extra-icons loading.
//
// This bundle is built with `@runtypelabs/persona/icons-extra` external (see
// `tsdown.config.ts`): the config-only tail of the icon registry is kept
// out of the CDN payload and fetched the first time `renderLucideIcon` is
// asked for one of its names. Same sibling-URL scheme as the chunks above.
// ---------------------------------------------------------------------------

import { setIconsExtraLoader } from "./icons-extra-loader";

setIconsExtraLoader(
  siblingChunk(
    "icons-extra.js",
    "host icons-extra.js alongside it, or register the icons they use via registerIcons()",
  ),
);

// ---------------------------------------------------------------------------
// Deferred WebMCP bridge runtime loading.
//
// This bundle is built with `@runtypelabs/persona/webmcp-runtime` external
// (see `tsdown.config.ts`): the WebMcpBridge class is kept out of the
// CDN payload and fetched by client.ts only when `config.webmcp.enabled` is
// true. Same sibling-URL scheme as the chunks above. (The polyfill itself is
// a separate, older chunk: `webmcp-polyfill.js`, registered further up.)
// ---------------------------------------------------------------------------

import { setWebMcpRuntimeLoader } from "./webmcp-runtime-loader";

setWebMcpRuntimeLoader(siblingChunk("webmcp-runtime.js"));

// ---------------------------------------------------------------------------
// Deferred session-reconnect loading.
//
// This bundle is built with `@runtypelabs/persona/session-reconnect` external
// (see `tsdown.config.ts`): the durable-session reconnect loop is kept
// out of the CDN payload and fetched only when a session with a
// `reconnectStream` transport first needs to resume. Same sibling-URL scheme
// as the chunks above.
// ---------------------------------------------------------------------------

import { setSessionReconnectLoader } from "./session-reconnect-loader";

setSessionReconnectLoader(siblingChunk("session-reconnect.js"));

// ---------------------------------------------------------------------------
// Deferred event-stream-view loading.
//
// Same scheme as history-view: `@runtypelabs/persona/event-stream-view` is
// external here (see `tsdown.config.ts`), and the observability panel is
// fetched from a sibling URL only when the visitor first opens it.
// ---------------------------------------------------------------------------

import { setEventStreamViewLoader } from "./event-stream-view-loader";

setEventStreamViewLoader(siblingChunk("event-stream-view.js"));

import { setComposerActionsLoader } from "./composer-actions-loader";
setComposerActionsLoader(siblingChunk("composer-actions-runtime.js"));

import { setDictationRuntimeLoader } from "./dictation-runtime-loader";
setDictationRuntimeLoader(siblingChunk("dictation-runtime.js"));

import { setBackgroundRuntimeLoader } from "./background-runtime-loader";
setBackgroundRuntimeLoader(siblingChunk("background-runtime.js"));

import { setConfigDiagnosticsLoader } from "./config-diagnostics-loader";
setConfigDiagnosticsLoader(siblingChunk("config-diagnostics.js"));

import { setActivityUiLoader } from "./activity-ui-loader";
setActivityUiLoader(siblingChunk("activity-ui.js"));

import { setLoadingLabelRuntimeLoader } from "./loading-label-runtime-loader";
setLoadingLabelRuntimeLoader(siblingChunk("loading-label-runtime.js"));

// Request construction and dispatch are needed only when sending a turn.
import { setClientDispatchLoader } from "./client-dispatch-loader";
setClientDispatchLoader(siblingChunk("client-dispatch.js"));

import { setTurnActivityUiLoader } from "./turn-activity-ui-loader";
setTurnActivityUiLoader(siblingChunk("turn-activity-ui.js"));
